import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { autoriaDoUsuarioLogado } from '../utils/recebimentoAutoria';
import { log, error as logError } from '../utils/DebugLogger';
import '../contracts/Recebimentos_SaveInput';
import '../contracts/Recebimentos_SaveResponse';

import '../contracts/FormasPagamento_ListaResponse';
import '../contracts/Orders_DeliverItemResponse';
import '../contracts/Orders_GetResponse';
import '../contracts/Orders_ItemsResponse';
import '../contracts/Orders_ListaResponse';
import '../contracts/Orders_EntregasKpisResponse';
import '../contracts/Orders_EntregasItemsResponse';
import '../contracts/Orders_PickupNotificationCreateInput';
import '../contracts/Orders_PickupNotificationCreateResponse';
import '../contracts/Orders_PickupNotificationsResponse';
import type { Recebimentos_SaveInput } from '../contracts/Recebimentos_SaveInput';
import type { Orders_EntregasKpisResponseDados } from '../contracts/Orders_EntregasKpisResponse';
import type { Orders_EntregasItemRow } from '../contracts/Orders_EntregasItemRow';
import type { Orders_PickupNotificationCreateInput } from '../contracts/Orders_PickupNotificationCreateInput';
/**
 * Composable for PDV Entregas (Deliveries) operations.
 * Manages delivery tracking and payment registration
 * for the Entregas panel in the PDV flow.
 *
 * Reuses SO endpoints and the recebimentos endpoint for payment registration.
 */
export function useSOEntregas() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/orders';

  /**
   * Fetch orders ready for delivery (ready or partially delivered status).
   * Fetches all orders and filters client-side for delivery-relevant statuses.
   */
  const fetchDeliveryOrders = async () => {
    log('useSOEntregas', 'fetchDeliveryOrders', 'Fetching delivery orders');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Orders_ListaResponse' });
      if (response.success) {
        const allOrders = response.dados || [];
        const deliveryStatuses = ['pronto_para_entrega', 'parcialmente_entregue'];
        return allOrders.filter((order: any) => deliveryStatuses.includes(order.so_order_status));
      }
      throw new Error(response.error || 'Erro ao buscar ordens');
    } catch (err) {
      logError('useSOEntregas', 'fetchDeliveryOrders', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get a single order by ID.
   */
  const getOrder = async (orderId: number) => {
    log('useSOEntregas', 'getOrder', 'Fetching order', { orderId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/get/${orderId}`, { responseContract: 'Orders_GetResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar ordem');
    } catch (err) {
      logError('useSOEntregas', 'getOrder', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch items for a specific order.
   */
  const fetchOrderItems = async (orderId: number) => {
    log('useSOEntregas', 'fetchOrderItems', 'Fetching items', { orderId });
    try {
      const response = await httpService.get(`${basePath}/items/${orderId}`, { responseContract: 'Orders_ItemsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar itens da ordem');
    } catch (err) {
      logError('useSOEntregas', 'fetchOrderItems', 'Error', err);
      throw err;
    }
  };

  /**
   * Deliver a specific service item.
   * @param {number} itemId - so_order_items PK
   */
  const deliverItem = async (itemId: number) => {
    log('useSOEntregas', 'deliverItem', 'Delivering item', { itemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/deliver_item/${itemId}`, null, { responseContract: 'Orders_DeliverItemResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao entregar item');
    } catch (err) {
      logError('useSOEntregas', 'deliverItem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Deliver multiple order items in parallel. Each call hits the existing
   * single-item endpoint POST /so/orders/deliver_item/{id}. v1 — not atomic;
   * a backend batch endpoint can replace this later.
   *
   * @param itemIds list of so_order_items PKs to mark as delivered
   * @returns the list of delivered_at timestamps in the same order as itemIds
   */
  const bulkDeliverItems = async (itemIds: number[]): Promise<unknown[]> => {
    log('useSOEntregas', 'bulkDeliverItems', 'Bulk delivering', { count: itemIds.length });
    if (itemIds.length === 0) return [];
    return Promise.all(itemIds.map((id) => deliverItem(id)));
  };

  /**
   * Register a payment using the existing recebimentos/save endpoint.
   * @param {Object} data - Payment data with fields:
   *   - recebimento_origem_id: order ID
   *   - recebimento_franquia_id: franchise ID
   *   - recebimento_forma_pagamento_id: payment method ID
   *   - recebimento_valor: amount in centavos
   *   - recebimento_data: date string YYYY-MM-DD
   */
  const registerPayment = async (data: any) => {
    log('useSOEntregas', 'registerPayment', 'Registering payment', data);
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Recebimentos_SaveInput = {
        recebimento_id: 0,
        recebimento_origem: 'so_order',
        recebimento_origem_id: data.recebimento_origem_id,
        recebimento_franquia_id: data.recebimento_franquia_id,
        recebimento_forma_pagamento_id: data.recebimento_forma_pagamento_id,
        recebimento_valor: data.recebimento_valor,
        recebimento_data: data.recebimento_data,
        recebimento_provedor: 'manual',
        // Autoria vem do lugar único — era AQUI que faltava o id do autor
        // (chamado 2026-0033).
        ...autoriaDoUsuarioLogado(),
      };
      const response = await httpService.post('/index.php/recebimentos/save', payload, {
        inputContract: 'Recebimentos_SaveInput',
        responseContract: 'Recebimentos_SaveResponse'
      });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao registrar pagamento');
    } catch (err) {
      logError('useSOEntregas', 'registerPayment', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available payment methods from the catalog.
   * Returns array of { forma_pagamento_id, forma_pagamento_nome, forma_pagamento_codigo }.
   */
  const fetchPaymentMethods = async () => {
    log('useSOEntregas', 'fetchPaymentMethods', 'Fetching payment methods');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get('/index.php/config/formas_pagamento/lista', { responseContract: 'FormasPagamento_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar formas de pagamento');
    } catch (err) {
      logError('useSOEntregas', 'fetchPaymentMethods', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const basePath2 = '/index.php/so/entregas';

  /**
   * Fetch KPIs for the /pdv/entregas page.
   * Returns five aggregated counts: today_and_next_2_days, delayed, rescheduled,
   * pending_alerts, and pending_delivery.
   */
  const fetchEntregasKpis = async (): Promise<Orders_EntregasKpisResponseDados> => {
    log('useSOEntregas', 'fetchEntregasKpis', 'Fetching KPIs');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath2}/kpis`, { responseContract: 'Orders_EntregasKpisResponse' });
      if (response.success) {
        return response.dados ?? {
          today_and_next_2_days: 0,
          delayed: 0,
          rescheduled: 0,
          pending_alerts: 0,
          pending_delivery: 0,
        };
      }
      throw new Error(response.error || 'Erro ao buscar KPIs de entrega');
    } catch (err) {
      logError('useSOEntregas', 'fetchEntregasKpis', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch delivery items — items with enriched status information
   * including notification history and delivery state.
   */
  const fetchEntregasItems = async (): Promise<Orders_EntregasItemRow[]> => {
    log('useSOEntregas', 'fetchEntregasItems', 'Fetching entrega items');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath2}/items`, { responseContract: 'Orders_EntregasItemsResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar itens de entrega');
    } catch (err) {
      logError('useSOEntregas', 'fetchEntregasItems', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Mark an order item as picked up / notified.
   * Records a pickup notification for the given item.
   * @param orderItemId - so_order_items PK
   * @param channel - Notification channel ('whatsapp' | 'instagram' | 'manual' | 'outro')
   * @param notes - Optional internal notes about the notification
   * @param messageId - Optional ID of the outgoing message (WhatsApp/Instagram)
   */
  const markPickupNotified = async (
    orderItemId: number,
    channel: 'whatsapp' | 'instagram' | 'manual' | 'outro' = 'manual',
    notes?: string,
    messageId?: number
  ): Promise<number> => {
    log('useSOEntregas', 'markPickupNotified', 'Marking item notified', { orderItemId, channel });
    isLoading.value = true;
    error.value = null;
    try {
      const payload: Orders_PickupNotificationCreateInput = {
        order_item_id: orderItemId,
        channel,
        ...(notes && { notes }),
        ...(messageId && { message_id: messageId })
      };
      const response = await httpService.post(`${basePath2}/pickup_notifications`, payload, {
        inputContract: 'Orders_PickupNotificationCreateInput',
        responseContract: 'Orders_PickupNotificationCreateResponse'
      });
      if (response.success && response.dados) {
        return response.dados.notification_id;
      }
      throw new Error(response.error || 'Erro ao registrar notificação');
    } catch (err) {
      logError('useSOEntregas', 'markPickupNotified', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Send a "ready for pickup" notification through the customers portal
   * (plano-gatilhos-notificacao.md §4, Trigger B) for a single order item.
   *
   * Kept SEPARATE from markPickupNotified() rather than folding 'portal' into
   * its `channel` union: that function's contract (a bare notification_id
   * number) is asserted by tests/contract/specs/useSOEntregas.spec.ts and used
   * by the untouched WhatsApp flow — this is the one channel whose result
   * carries more than an id (portal_notification_id + the three-outcome
   * `warning`), so it gets its own return shape instead of changing that one.
   *
   * Per-item by design: the caller (PDVEntregasNotifyModal) reports N sent /
   * which failed rather than one aggregate toast — a portal failure on one
   * item must never look like it also failed for the others.
   */
  const sendPortalPickupNotification = async (orderItemId: number): Promise<{
    notificationId: number;
    portalNotificationId: string | null;
    warning: string | null;
  }> => {
    log('useSOEntregas', 'sendPortalPickupNotification', 'Sending portal pickup notification', { orderItemId });
    try {
      const payload: Orders_PickupNotificationCreateInput = {
        order_item_id: orderItemId,
        channel: 'portal',
      };
      const response = await httpService.post(`${basePath2}/pickup_notifications`, payload, {
        inputContract: 'Orders_PickupNotificationCreateInput',
        responseContract: 'Orders_PickupNotificationCreateResponse'
      });
      if (response.success && response.dados) {
        return {
          notificationId: response.dados.notification_id,
          portalNotificationId: response.dados.portal_notification_id ?? null,
          warning: response.dados.warning ?? null,
        };
      }
      throw new Error(response.error || 'Erro ao enviar notificação pelo portal');
    } catch (err) {
      logError('useSOEntregas', 'sendPortalPickupNotification', 'Error', err);
      throw err;
    }
  };

  /**
   * Fetch pickup notification history for a single order item.
   */
  const fetchPickupNotifications = async (itemId: number) => {
    log('useSOEntregas', 'fetchPickupNotifications', 'Fetching pickup notifications', { itemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath2}/pickup_notification_history/${itemId}`, { responseContract: 'Orders_PickupNotificationsResponse' });
      if (response.success) {
        return response.dados;
      }
      throw new Error(response.error || 'Erro ao buscar notificacoes');
    } catch (err) {
      logError('useSOEntregas', 'fetchPickupNotifications', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchDeliveryOrders,
    getOrder,
    fetchOrderItems,
    deliverItem,
    bulkDeliverItems,
    registerPayment,
    fetchPaymentMethods,
    fetchEntregasKpis,
    fetchEntregasItems,
    fetchPendingPickupAlerts: fetchEntregasItems, // legacy alias for backward compat
    markPickupNotified,
    sendPortalPickupNotification,
    fetchPickupNotifications,
  };
}
